import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Text } from '@common/components';
import { datetime } from '@common/services/datetime';

/** How often the "Saved · {relative time}" label refreshes. Exported for specs. */
export const RELATIVE_TIME_REFRESH_MS = 5000;
/** Saves younger than this read "just now". */
const JUST_NOW_THRESHOLD_SECONDS = 10;

export type SavingTimeProps = {
  /** A save is in flight — always shows "Saving…". */
  saving?: boolean;
  /** Last successful save. Nothing is rendered while it's empty and no save is in flight. */
  lastSavedAt?: Date | null;
  className?: string;
  dataTestId?: string;
};

/**
 * Save-state feedback label: "Saving…" while a save is in flight, then
 * "Saved · {relative time}" with a self-refreshing relative time ("just now"
 * under 10 seconds, dayjs wording after). Renders nothing before the first
 * save unless a save is in flight.
 */
export const SavingTime = ({
  saving = false,
  lastSavedAt = null,
  className,
  dataTestId,
}: SavingTimeProps) => {
  // Re-render periodically so the relative time stays fresh.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!lastSavedAt || saving) return undefined;
    const interval = setInterval(
      () => setNow(new Date()),
      RELATIVE_TIME_REFRESH_MS
    );
    return () => clearInterval(interval);
  }, [lastSavedAt, saving]);

  const label = buildLabel(saving, lastSavedAt, now);

  if (!label) return null;

  return (
    <Text
      size="md"
      secondary
      className={clsx('wk-saving-time', className)}
      dataTestId={dataTestId}
    >
      {label}
    </Text>
  );
};

function buildLabel(saving: boolean, lastSavedAt: Date | null, now: Date) {
  if (saving) return 'Saving…';
  if (lastSavedAt) return `Saved · ${formatRelativeTime(lastSavedAt, now)}`;
  return null;
}

function formatRelativeTime(date: Date, now: Date): string {
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (seconds < JUST_NOW_THRESHOLD_SECONDS) return 'just now';

  return datetime(date).from(datetime(now));
}
